import { z } from "zod/v4";

const Auth0ConfigSchema = z.object({
	__comment: z.string().optional(), // allow optional metadata comments
	auth0Domain: z.string().min(1, "auth0Domain is required"),
	auth0ClientId: z.string().min(1, "auth0ClientId is required"),
	audience: z.url("audience must be a valid URL"),
	// Public client ID for published docs token refresh (no client secret required)
	publishAuth0ClientId: z.string().min(1, "publishAuth0ClientId is required"),
});

const ModeSchema = z.enum(["web", "desktop", "mobile", "ssr"]).default("web");

const ConfigSchema = z.object({
	appEnv: z.enum(["local", "staging", "production"]).default("local"),
	mode: ModeSchema,
	vercelUrl: z.string().default("https://git-staging.moment.dev"),
	webUrl: z.string().default("https://docs-staging.moment.dev"),
	docsApiUrl: z.string().default("https://git-staging.moment.dev"),
	giteaApiUrl: z.string().default("https://git-staging.moment.dev/api/v1"),
	giteaHostUrl: z.string().default("git-staging.moment.dev"),
	secretsProxyUrl: z.string().default("https://shh-staging.moment.dev"),
	authSettings: z.object({
		auth0Domain: Auth0ConfigSchema.shape.auth0Domain,
		auth0ClientId: Auth0ConfigSchema.shape.auth0ClientId,
		audience: Auth0ConfigSchema.shape.audience,
		publishAuth0ClientId: Auth0ConfigSchema.shape.publishAuth0ClientId,
		// can be a deep-link
		loginRedirectURL: z.string().default("https://docs-staging.moment.dev/login"),
		logoutRedirectURL: z.string().default("https://docs-staging.moment.dev/logout"),
	}),
});

export type Mode = z.infer<typeof ModeSchema>;

const STAGING_DOCS_HOSTNAME = "docs-staging.moment.dev";
const PROD_DOCS_HOSTNAME = "docs.moment.dev";

const LOCAL_BASE_URL_DEFAULT = "http://localhost:3333";
const STAGING_BASE_URL_DEFAULT = `https://${STAGING_DOCS_HOSTNAME}`;
const PROD_BASE_URL_DEFAULT = `https://${PROD_DOCS_HOSTNAME}`;

export const STAGING_IFRAME_BASE_URL = "https://iframe-staging.moment.dev";

export type Auth0Config = z.infer<typeof Auth0ConfigSchema>;

export type EnvConfig = z.infer<typeof ConfigSchema> & {
	isDesktopApp: boolean;
	isMobileApp: boolean;
	isSsr: boolean;
	isFilesystemAvailable: boolean;
	isInIframe: boolean;
	isWebApp: boolean;
	isWindows: boolean;
	platform: NodeJS.Platform | undefined;
	connectionCheckUrl: string;
	appVersion: string | undefined;
	userAgent: string | undefined;
};

/**
 * Get the browser-accessible URL for published documents.
 * This always returns an https:// URL, even when running in desktop mode.
 */
export const getPublishWebUrl = (appEnv: string): string => {
	switch (appEnv) {
		case "production":
			return PROD_BASE_URL_DEFAULT;
		case "staging":
		default:
			return STAGING_BASE_URL_DEFAULT;
	}
};

// Helper to get the docs API URL based on environment
const getDocsApiUrl = (appEnv: string, giteaApiUrl?: string): string => {
	// Check for explicit override via environment variable
	if (typeof giteaApiUrl === "string" && giteaApiUrl.length > 0) {
		if (giteaApiUrl.startsWith("http")) {
			return giteaApiUrl;
		}
		return `https://${giteaApiUrl}`;
	}

	if (appEnv === "production") {
		return "https://git.moment.dev";
	}

	return "https://git-staging.moment.dev";
};

export const getWebBaseURL = (env: string, mode: string): string => {
	if (mode === "desktop" || mode === "mobile") {
		switch (env) {
			case "production":
				return "moment-app://-";
			case "staging":
				return "moment-app-staging://-";
			default:
				return "moment-app-dev://-";
		}
	}

	const currentBaseUrl =
		typeof window === "undefined"
			? undefined
			: `${window.location.protocol}//${window.location.host}`;

	// On local, force to local urls (instead of passed env)
	if (currentBaseUrl === LOCAL_BASE_URL_DEFAULT) {
		return LOCAL_BASE_URL_DEFAULT;
	}

	switch (env) {
		case "staging":
			// Allow current urls for vercel deploys, otherwise use web-staging
			return currentBaseUrl ?? STAGING_BASE_URL_DEFAULT;
		case "production":
			return currentBaseUrl ?? PROD_BASE_URL_DEFAULT;
		case "local":
		case "testing":
		default:
			return currentBaseUrl ?? LOCAL_BASE_URL_DEFAULT;
	}
};

const getLoginRedirectURL = (env: string, mode: string): string => {
	return `${getWebBaseURL(env, mode)}/login`;
};

/**
 * Get the tldraw sync server base URL based on environment.
 */
export const getTldrawBaseUrl = (appEnv: string): string => {
	switch (appEnv) {
		case "production":
			return "https://tldraw-worker-production.moment.dev";
		case "staging":
		default:
			return "https://tldraw-worker-staging.moment.dev";
	}
};

// Base URL for the secretsproxy service. Both the secret-management API
// (/internal/v1/secrets) and the request-proxying endpoint live here.
export const getSecretsProxyBaseUrl = (appEnv: string): string => {
	switch (appEnv) {
		case "production":
			return "https://shh.moment.dev";
		case "staging":
		case "local":
		default:
			return "https://shh-staging.moment.dev";
	}
};

const getLogoutRedirectURL = (env: string, mode: string): string => {
	return `${getWebBaseURL(env, mode)}/logout`;
};

export const getWebAuthConfigs = (env: string): Auth0Config => {
	switch (env) {
		case "production": {
			return Auth0ConfigSchema.parse(
				require("../_interop/auth-settings/auth-env-vars.web.production.json")
			);
		}
		case "staging": {
			return Auth0ConfigSchema.parse(
				require("../_interop/auth-settings/auth-env-vars.web.staging.json")
			);
		}
		default: {
			return Auth0ConfigSchema.parse(
				require("../_interop/auth-settings/auth-env-vars.web.development.json")
			);
		}
	}
};

const getDefaultEnv = (): EnvConfig => {
	const docsApiUrl = getDocsApiUrl(
		process.env["NEXT_PUBLIC_APP_ENV"] || process.env["APP_ENV"]!,
		process.env["NEXT_PUBLIC_GITEA_API_URL"] || process.env["GITEA_API_URL"]
	);
	const parsedDocsApiUrl = new URL(docsApiUrl);
	const giteaHostUrl = parsedDocsApiUrl.host;
	const giteaApiUrl = process.env["NEXT_PUBLIC_GITEA_API_URL"] || process.env["GITEA_API_URL"];
	const mode = process.env["MODE"] || "web";
	const authConfigs = getWebAuthConfigs(process.env["AUTH_ENV"]!);

	const config = ConfigSchema.parse({
		appEnv: process.env["APP_ENV"],
		vercelUrl: process.env["VERCEL_URL"],
		webUrl: getWebBaseURL(process.env["APP_ENV"]!, mode),
		mode,
		docsApiUrl,
		giteaApiUrl,
		giteaHostUrl,
		secretsProxyUrl: getSecretsProxyBaseUrl(process.env["APP_ENV"] ?? "local"),
		authSettings: {
			audience: authConfigs.audience,
			auth0Domain: authConfigs.auth0Domain,
			auth0ClientId: authConfigs.auth0ClientId,
			publishAuth0ClientId: authConfigs.publishAuth0ClientId,
			loginRedirectURL: getLoginRedirectURL(process.env["APP_ENV"]!, mode),
			logoutRedirectURL: getLogoutRedirectURL(process.env["APP_ENV"]!, mode),
		},
	});

	const isWindows =
		(typeof process !== "undefined" && process.platform === "win32") ||
		(typeof navigator !== "undefined" && navigator.userAgent.includes("Windows"));

	return {
		...config,
		isDesktopApp: config.mode === "desktop",
		isMobileApp: config.mode === "mobile",
		isSsr: config.mode === "ssr" || typeof window === "undefined",
		isFilesystemAvailable:
			config.mode !== "ssr" && (config.mode === "desktop" || config.mode === "mobile"),
		isInIframe: typeof window === "undefined" ? false : window.parent !== window.self,
		isWebApp: config.mode === "web",
		isWindows,
		platform: process.env["PLATFORM"] as NodeJS.Platform | undefined,
		connectionCheckUrl: `${config.docsApiUrl}/healthcheck`,
		appVersion: process.env["APP_SHA"] || undefined,
		userAgent: process.env["APP_SHA"] ? `Moment/${process.env["APP_SHA"]}` : undefined,
	};
};

// Read-only environment object.
export const env = getDefaultEnv();

/**
 * Initializes `env.appVersion` and `env.userAgent` from Tauri.
 * Call once at app startup (e.g. in `_app.tsx`).
 *
 * - `env.appVersion` is the raw version string (e.g. `0.3.667`)
 * - `env.userAgent` is derived as `Moment/<version>` (e.g. `Moment/0.3.667`)
 */
export const initAppVersion = async (): Promise<void> => {
	try {
		const { getVersion } = await import("@tauri-apps/api/app");
		env.appVersion = await getVersion();
		env.userAgent = `Moment/${env.appVersion}`;
	} catch {
		// Tauri API not available — leave as undefined
	}
};
